import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Fab } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { fetchAgencias, toggleAgenciaStatus } from '../../../../../actions/traslados';
import { getArtPrestadores } from '../../../../../actions/arts';
import AgenciasTable from './AgenciasTable';
import AgenciaFormDialog from './AgenciaFormDialog';
import ConfirmDialog from '../../../../commons/dialog/confirmDialog';
import snackbarUtils from '../../../../../utils/snackbarUtils';
import AgenciasFilter from './AgenciasFilter';
import { StyledTraslados } from '../../../../styled/traslados';

const AgenciasContainer = () => {
    const dispatch = useDispatch();
    const { data: agencias, isFetching } = useSelector(state => state.traslados.agencias);
    
    const initialFilters = { razonSocial: '', estado: 'activas' };
    const [filters, setFilters] = useState(initialFilters);
    
    const [openForm, setOpenForm] = useState(false);
    const [agenciaToEdit, setAgenciaToEdit] = useState(null);
    
    const [openConfirm, setOpenConfirm] = useState(false);
    const [agenciaToToggle, setAgenciaToToggle] = useState(null);
    const [confirmMessage, setConfirmMessage] = useState('');

    const artPrestadores = useSelector(state => state.arts.artPrestadores);

    useEffect(() => {
        dispatch(fetchAgencias(filters.estado, filters.razonSocial));
    }, [dispatch, filters]);

    useEffect(() => {
        if (!artPrestadores?.data?.length && !artPrestadores?.isFetching) {
            dispatch(getArtPrestadores());
        }
    }, [dispatch, artPrestadores?.data?.length, artPrestadores?.isFetching]);

    const handleSearch = (values) => {
        setFilters(values);
    };

    const handleClear = () => {
        setFilters(initialFilters);
    };

    const handleOpenForm = (agencia = null) => {
        setAgenciaToEdit(agencia);
        setOpenForm(true);
    };

    const handleCloseForm = () => {
        setOpenForm(false);
        setAgenciaToEdit(null);
        dispatch(fetchAgencias(filters.estado, filters.razonSocial));
    };

    const handleToggleStatus = (agencia) => {
        setAgenciaToToggle(agencia);
        setConfirmMessage(`¿Está seguro que desea ${agencia.activo ? 'desactivar' : 'activar'} la agencia ${agencia.razonSocial}?`);
        setOpenConfirm(true);
    };

    const handleConfirmToggle = () => {
        if (!agenciaToToggle) return;
        
        dispatch(toggleAgenciaStatus(agenciaToToggle.idAgencia, !agenciaToToggle.activo, false))
            .then(res => {
                const responseData = res.value.data;
                if (responseData.success === false && responseData.hasActiveTramos) {
                    setConfirmMessage(`${responseData.message}`);
                } else {
                    snackbarUtils.success(`Agencia ${!agenciaToToggle.activo ? 'activada' : 'desactivada'} correctamente`);
                    setOpenConfirm(false);
                    setAgenciaToToggle(null);
                    dispatch(fetchAgencias(filters.estado, filters.razonSocial));
                }
            })
            .catch(() => {
                snackbarUtils.error('Error al cambiar el estado de la agencia');
                setOpenConfirm(false);
            });
    };

    const handleForceToggle = () => {
        if (!agenciaToToggle) return;
        
        dispatch(toggleAgenciaStatus(agenciaToToggle.idAgencia, !agenciaToToggle.activo, true))
            .then(() => {
                snackbarUtils.success(`Agencia desactivada correctamente`);
                setOpenConfirm(false);
                setAgenciaToToggle(null);
                dispatch(fetchAgencias(filters.estado, filters.razonSocial));
            })
            .catch(() => {
                snackbarUtils.error('Error al cambiar el estado de la agencia');
                setOpenConfirm(false);
            });
    }

    return (
        <Box p={2}>
            <StyledTraslados.Title variant="h6" gutterBottom>
                Agencias de Traslado
            </StyledTraslados.Title>
            
            <AgenciasFilter 
                initialValues={filters} 
                onSearch={handleSearch} 
                onClear={handleClear} 
            />

            <AgenciasTable 
                data={agencias} 
                loading={isFetching}
                onEdit={handleOpenForm}
                onToggleStatus={handleToggleStatus}
            />
            
            <Fab 
                color="primary" 
                aria-label="add" 
                onClick={() => handleOpenForm()}
                sx={{ position: 'fixed', bottom: 32, right: 32, zIndex: 1000 }}
            >
                <AddIcon />
            </Fab>

            <AgenciaFormDialog 
                open={openForm}
                onClose={handleCloseForm}
                agencia={agenciaToEdit}
            />

            <ConfirmDialog
                open={openConfirm}
                handleClose={() => setOpenConfirm(false)}
                handleConfirm={() => {
                    if (confirmMessage.includes("tramos activos en")) {
                        handleForceToggle();
                    } else {
                        handleConfirmToggle();
                    }
                }}
                title="Confirmar Acción"
                text={confirmMessage}
                loading={isFetching}
            />
        </Box>
    );
};

export default AgenciasContainer;
